import { Button, CheckCircle, RefreshCw, XCircle } from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  setAppointmentToCancel,
  setShowCancelModal,
} from "../../../store/reducers/appointmentSlice";
import {
  Appointment,
  AppointmentStatusEnum,
} from "../../../types/appointment.definition";

interface AppointmentActionsProps {
  appointment: Appointment;
  status: string;
  isUpdating: boolean;
  onStatusChange: (appointmentId: string, newStatus: string) => void;
  showCancel?: boolean;
  fullWidth?: boolean;
}

export const AppointmentActions: React.FC<AppointmentActionsProps> = ({
  appointment,
  status,
  isUpdating,
  onStatusChange,
  showCancel = true,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Check if appointment time is in the past
  const isAppointmentInPast = React.useMemo(() => {
    if (!appointment.appointmentTime) return false;
    const appointmentTime = new Date(appointment.appointmentTime);
    const now = new Date();
    return appointmentTime < now;
  }, [appointment.appointmentTime]);

  const handleCancelClick = () => {
    dispatch(setAppointmentToCancel(appointment));
    dispatch(setShowCancelModal(true));
  };

  if (isUpdating) {
    return (
      <div className="flex items-center text-sm text-gray-500">
        <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
        {t("appointment.manage.actions.updating")}
      </div>
    );
  }

  const containerClassName = fullWidth
    ? "flex flex-col space-y-2"
    : "flex justify-end space-x-2";

  return (
    <div className={containerClassName}>
      {showCancel &&
        status === AppointmentStatusEnum.CONFIRMED &&
        !isAppointmentInPast && (
          <Button
            variant="ghost"
            size={fullWidth ? undefined : "sm"}
            onClick={handleCancelClick}
            className={
              fullWidth
                ? "justify-start w-full text-red-600 hover:text-red-700"
                : "text-red-600 hover:text-red-700"
            }
          >
            <XCircle className="mr-2 w-4 h-4" />
            {t("appointment.manage.actions.cancel")}
          </Button>
        )}

      {status === AppointmentStatusEnum.IN_PROGRESS && (
        <Button
          variant="ghost"
          size={fullWidth ? undefined : "sm"}
          onClick={() =>
            onStatusChange(appointment.id!, AppointmentStatusEnum.COMPLETE)
          }
          className={
            fullWidth
              ? "justify-start w-full text-purple-600 hover:text-purple-700"
              : "text-purple-600 hover:text-purple-700"
          }
        >
          <CheckCircle className="mr-2 w-4 h-4" />
          {t("appointment.manage.actions.complete")}
        </Button>
      )}
    </div>
  );
};
